import type { IProduct, IProductToSave } from '@/types/product';

const DESCRIPTION_MAX = 120;

export interface CatalogRowFlags {
  excludedIds: number[];
  cartIds: number[];
}

/** Strip the `***currency***` markers used in allocation_label HTML. */
export function stripAllocationLabel(label: string | undefined | null): string {
  return (label || '')
    .replace(/\*\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function truncateDescription(text: string | undefined | null, max = DESCRIPTION_MAX): string {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/** Integer cents → `"199 CHF"` (no locale). */
export function formatMajorAmount(cents: number | undefined | null, currency?: string | null): string {
  if (cents == null || Number.isNaN(Number(cents))) return '';
  const major = Number(cents) / 100;
  const n = Number.isInteger(major) ? String(major) : major.toFixed(2);
  return currency ? `${n} ${currency}` : n;
}

function flattenFeatures(features: IProduct['key_features'] | undefined): string {
  if (!features || typeof features !== 'object') return '';
  return Object.entries(features)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
}

function formatPrice(product: IProduct): string {
  switch (product.type) {
    case 'single':
      return formatMajorAmount(product.price, product.price_currency);
    case 'variable': {
      const min = formatMajorAmount(product.budget_min);
      const max = formatMajorAmount(product.budget_max, product.budget_currency);
      if (min && max) return `${min}–${max}`;
      return max || min;
    }
    case 'target':
      return formatMajorAmount(product.spending_limit, product.spending_limit_currency);
    default:
      return product.duration != null ? `${product.duration} days` : '';
  }
}

function formatRemaining(product: IProduct): string {
  if (product.type === 'contingent') {
    const uses = product.uses ?? 0;
    const spent = product.uses_spent ?? 0;
    const refill = (product.refill_interval || 0) > 0 ? 'auto-refill' : 'non-refill';
    return `${Math.max(0, uses - spent)} of ${uses} uses (${refill})`;
  }
  if (product.type === 'subscription') {
    const slots = product.slots ?? 0;
    const used = product.slots_used ?? 0;
    return `${Math.max(0, slots - used)} of ${slots} slots`;
  }
  return '';
}

export function toCatalogRow(product: IProduct, flags: CatalogRowFlags) {
  const inCart = flags.cartIds.includes(product.id);
  const excluded = flags.excludedIds.includes(product.id);

  return {
    id: product.id,
    name: product.name,
    type: product.type,
    description: truncateDescription(product.description),
    label: stripAllocationLabel(product.allocation_label),
    sublabel: product.allocation_sublabel || '',
    features: flattenFeatures(product.key_features),
    providers: (product.campaign_providers || []).join(', '),
    price: formatPrice(product),
    remaining: formatRemaining(product),
    available: !excluded,
    in_cart: inCart,
    excludes_all: !!product.excludes_all,
    excludes: (product.excludes || []).join(', '),
    exclude_types: (product.exclude_types || []).join(', '),
  };
}

export function toCartRow(product: IProductToSave) {
  return {
    id: product.id,
    name: product.name || '',
    startdate: product.startdate || '',
    enddate: product.enddate || '',
    budget: formatMajorAmount(product.budget, product.currency),
    total: formatMajorAmount(product.totalPrice, product.currency),
    currency: product.currency || '',
  };
}
